import React from "react";
import { Link as RouterLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";

export default function PrivacyPolicy() {
    const theme = useTheme();

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.light}15 100%)`,
            }}
        >
            {/* Navigation Bar */}
            <Box
                sx={{
                    py: 2,
                    px: { xs: 2, sm: 4 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            variant="h5"
                            component={RouterLink}
                            to="/"
                            sx={{
                                fontWeight: 800,
                                color: theme.palette.primary.main,
                                textDecoration: "none",
                            }}
                        >
                            TapSheet
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/"
                            variant="contained"
                            color="primary"
                        >
                            Go to Home
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Content */}
            <Container maxWidth="md" sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 800,
                        mb: 4,
                        fontSize: { xs: "2rem", md: "2.5rem" },
                    }}
                >
                    Privacy Policy
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 4 }}
                >
                    Last updated:{" "}
                    {new Date().toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    })}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            1. Introduction
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            Welcome to TapSheet ("we," "our," or "us"). We are
                            committed to protecting your privacy and ensuring
                            you have a positive experience while using our
                            service. This Privacy Policy explains how we
                            collect, use, disclose, and safeguard your
                            information when you use our expense tracking and
                            budgeting application on iOS mobile devices and web
                            browsers.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            2. Information We Collect
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            We collect information that you provide directly to
                            us, including:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Account information (email address, first
                                    name, last name, profile picture when using
                                    Google authentication)
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Transaction data (dates, amounts,
                                    descriptions, categories, transaction types,
                                    and flow types)
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Budgeting information (monthly savings
                                    targets, fixed and variable expense tracking)
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Subscription information (subscription status,
                                    product IDs, expiration dates, transaction
                                    IDs from Apple App Store and RevenueCat)
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Usage statistics (login times, streak data,
                                    and feature usage analytics)
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Payment information processed through Apple
                                    App Store (we do not store credit card
                                    details or full payment information)
                                </Typography>
                            </li>
                        </Box>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            3. How We Use Your Information
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            We use the information we collect to:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Provide, maintain, and improve our expense
                                    tracking and budgeting services
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Process transactions, calculate budgets, and
                                    generate monthly summaries
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Authenticate your account using Google Sign
                                    In, Apple Sign In, or email/password
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Process payments and manage subscriptions
                                    through Apple App Store (via RevenueCat)
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Track usage statistics and provide features
                                    like daily streaks
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Send you service-related communications and
                                    notifications
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Detect and prevent fraud or abuse
                                </Typography>
                            </li>
                        </Box>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            4. Data Storage and Security
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            We implement appropriate technical and
                            organizational measures to protect your personal
                            information. Your data is stored securely and
                            encrypted in transit. However, no method of
                            transmission over the Internet or electronic storage
                            is 100% secure, and we cannot guarantee absolute
                            security.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            5. Third-Party Services
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            We use the following third-party services:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    <strong>Google:</strong> For authentication
                                    (Google Sign In). Your use of Google
                                    services is subject to Google's Privacy
                                    Policy.
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    <strong>Apple:</strong> For authentication
                                    (Sign in with Apple on iOS devices). Your
                                    use of Apple services is subject to Apple's
                                    Privacy Policy.
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    <strong>RevenueCat:</strong> For managing
                                    iOS in-app subscriptions and purchase
                                    validation. Your subscription data is
                                    processed by RevenueCat in accordance with
                                    their Privacy Policy.
                                </Typography>
                            </li>
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                            These services have their own privacy policies and
                            terms of service governing the use of your
                            information. We encourage you to review their
                            policies to understand how they handle your data.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            6. Your Rights
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            You have the right to:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Access and receive a copy of your personal
                                    data
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Request correction of inaccurate data
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Request deletion of your data
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Object to processing of your data
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Withdraw consent at any time
                                </Typography>
                            </li>
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                            To exercise these rights, please contact us at{" "}
                            <Box
                                component="a"
                                href="mailto:snapsheetsocials@gmail.com"
                                sx={{
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    fontWeight: 600,
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                snapsheetsocials@gmail.com
                            </Box>
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            7. Data Retention
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            We retain your personal information for as long as
                            necessary to provide our services and fulfill the
                            purposes described in this policy. When you delete
                            your account, we will delete or anonymize your
                            personal data, subject to legal retention
                            requirements.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            8. Changes to This Privacy Policy
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            We may update this Privacy Policy from time to time.
                            We will notify you of any changes by posting the new
                            Privacy Policy on this page and updating the "Last
                            updated" date. You are advised to review this
                            Privacy Policy periodically for any changes.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            9. Contact Us
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            If you have any questions about this Privacy Policy,
                            please contact us at{" "}
                            <Box
                                component="a"
                                href="mailto:snapsheetsocials@gmail.com"
                                sx={{
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    fontWeight: 600,
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                snapsheetsocials@gmail.com
                            </Box>
                        </Typography>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
